/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Award,
	BadgeCheck,
	FileBadge,
	Leaf,
	Shield,
	ShieldCheck,
	Wrench,
	type LucideIcon,
} from "lucide-react";

import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import Seo from "@/components/Seo";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";

import {
	Card,
	GridLayout,
	PageHeader,
	PageShell,
	RevealOnScroll,
	SectionDivider,
	SectionWrapper,
} from "./Certifications.page-primitives";
import DotGrid from "@/components/customCopmonent/DotGrid";
import { PageHeaderHero, PAGE_HERO_CLASS } from "@/components/customCopmonent/Text/PageHeaderHero";

/* =========================
   STATIC DATA (non-translatable: images, icons)
========================= */

const bgimage = "/images/header/WorldWideProjects.JPG";

// Per-item image + icon, kept in fixed order; text resolves from the locale.
const certificateMeta: { image: string; icon: LucideIcon }[] = [
	{ image: "/images/certificate/b.png", icon: ShieldCheck },
	{ image: "/images/certificate/c.png", icon: FileBadge },
	{ image: "/images/certificate/g.png", icon: Wrench },
	{ image: "/images/certificate/iso-9001.png", icon: BadgeCheck },
	{ image: "/images/certificate/d.png", icon: Leaf },
	{ image: "/images/certificate/e.png", icon: Shield },
	{ image: "/images/certificate/f.png", icon: Award },
];

type CertificateItem = {
	name: string;
	agency: string;
	description: string;
	image: string;
	icon: LucideIcon;
};

/* =========================
   COMPONENT
========================= */

const Certifications = () => {
	const { t } = useTranslation();
	const [selected, setSelected] = useState<CertificateItem | null>(null);

	const textItems = t("aboutPages.certifications.items", {
		returnObjects: true,
	}) as { name: string; agency: string; description: string }[];

	const items: CertificateItem[] = textItems.map((item, index) => ({
		...item,
		image: certificateMeta[index]?.image ?? "",
		icon: certificateMeta[index]?.icon ?? ShieldCheck,
	}));

	const meta = t("aboutPages.certifications.meta", {
		returnObjects: true,
	}) as string[];

	return (
		<PageShell>
			<Seo
				titleKey="aboutPages.certifications.seo.title"
				descriptionKey="aboutPages.certifications.seo.description"
				path="/certifications"
			/>
			<Navigation />
			{/* HEADER */}{" "}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0 ">
					{" "}
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${bgimage})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
						proximity={100}
						shockRadius={200}
						shockStrength={3}
						resistance={750}
						returnDuration={1.5}
					/>
				</div>
				<PageHeaderHero
					// align="center"
					title={t("aboutPages.certifications.title")}
					description={t("aboutPages.certifications.tagline")}
					meta={meta}
				/>
			</SectionWrapper>
			<SectionDivider />
			{/* GRID */}
			<SectionWrapper className="pt-0">
				<GridLayout
					columns="four"
					className="mt-6">
					{items.map((item, index) => {
						const Icon = item.icon;

						return (
							<RevealOnScroll
								key={item.name}
								delay={index * 0.04}>
								<button
									type="button"
									onClick={() => setSelected(item)}
									className="w-full text-left">
									<Card className="group h-full overflow-hidden p-0 transition-all duration-300 hover:-translate-y-1">
										{/* IMAGE */}
										<div className="relative h-100 overflow-hidden">
											<img
												src={item.image}
												alt={item.name}
												className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
											/>

											<div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
										</div>

										{/* CONTENT */}
										<div className="p-4">
											<h3 className="text-sm font-semibold text-slate-900 line-clamp-2">
												{item.name}
											</h3>

											<p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-[#2d2f92]">
												{item.agency}
											</p>

											<p className="mt-2 text-xs leading-6 text-slate-600 line-clamp-3">
												{item.description}
											</p>

											<div className="mt-3 h-[2px] w-8 bg-gradient-to-r from-[#2d2f92] to-[#f9a93a]" />
										</div>
									</Card>
								</button>
							</RevealOnScroll>
						);
					})}
				</GridLayout>
			</SectionWrapper>
			{/* DIALOG */}
			<Dialog
				open={Boolean(selected)}
				onOpenChange={(open) => !open && setSelected(null)}>
				<DialogContent className="max-w-2xl h-[90%] rounded-3xl border-slate-200 bg-white p-0 text-slate-900">
					{selected && (
						<div className="overflow-hidden rounded-3xl">
							{/* HEADER */}
							<div className="border-b border-slate-200 px-6 py-5">
								<DialogHeader className="text-left">
									<DialogTitle className="text-xl font-semibold text-slate-900">
										{selected.name}
									</DialogTitle>
								</DialogHeader>
							</div>
							{/* IMAGE */}
							<div className="h-[90%] lg:h-[90%] w-full bg-slate-100">
								<img
									src={selected.image}
									alt={selected.name}
									className="h-full w-full object-contain"
								/>
							</div>
						</div>
					)}
				</DialogContent>
			</Dialog>
			<FooterSection />
		</PageShell>
	);
};

export default Certifications;
